import React, { useState } from 'react'
import {
    TextField,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    InputAdornment,
    IconButton,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import { Container } from '../../../../../../components'
import { ScheduleFormData, recurrencePatterns } from '../../types'
import { Company } from '../../../../../../models'
import {
    MuiPickersUtilsProvider,
    KeyboardDateTimePicker,
} from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'

interface Props {
    scheduleForm: ScheduleFormData
    onFormChange: (form: ScheduleFormData) => void
    theme: any
    companies: Company[]
    loading?: boolean
    showErrors: boolean
}

export const ScheduleForm: React.FC<Props> = ({
    scheduleForm,
    onFormChange,
    theme,
    companies,
    loading = false,
    showErrors,
}) => {
    const [searchTerm, setSearchTerm] = useState('')

    const filteredCompanies = companies.filter((company) =>
        company.name.toLowerCase().includes(searchTerm.toLowerCase()),
    )

    const handleCompanyToggle = (companyId: number) => {
        const newCompanies = scheduleForm.companies.includes(companyId)
            ? scheduleForm.companies.filter((id) => id !== companyId)
            : [...scheduleForm.companies, companyId]

        onFormChange({
            ...scheduleForm,
            companies: newCompanies,
        })
    }

    const isStartDateValid = (date: Date) => {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        return date >= today
    }

    const getDateFieldError = () => {
        if (!scheduleForm.start_date) return 'Start date is required'
        if (!isStartDateValid(scheduleForm.start_date))
            return 'Start date must be today or in the future'
        return ''
    }

    return (
        <Container style={{ gap: theme.spacing(3) }}>
            {/* Left Column - Form Fields */}
            <Container
                style={{
                    flexDirection: 'column',
                    gap: theme.spacing(2),
                    flex: 1,
                }}
            >
                <TextField
                    fullWidth
                    required
                    label="Name"
                    value={scheduleForm.name}
                    onChange={(e) =>
                        onFormChange({
                            ...scheduleForm,
                            name: e.target.value,
                        })
                    }
                    error={showErrors && !scheduleForm.name}
                    helperText={
                        showErrors && !scheduleForm.name
                            ? 'Name is required'
                            : ''
                    }
                />

                <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Description (Optional)"
                    value={scheduleForm.description}
                    onChange={(e) =>
                        onFormChange({
                            ...scheduleForm,
                            description: e.target.value,
                        })
                    }
                />

                <Container
                    style={{ flexDirection: 'column', gap: theme.spacing(1) }}
                >
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardDateTimePicker
                            required
                            fullWidth
                            minDate={new Date()}
                            label="Start Date & Time"
                            value={scheduleForm.start_date}
                            onChange={(date) =>
                                onFormChange({
                                    ...scheduleForm,
                                    start_date: date || new Date(),
                                })
                            }
                            error={
                                showErrors &&
                                !isStartDateValid(scheduleForm.start_date)
                            }
                            helperText={getDateFieldError()}
                            format="MM/dd/yyyy hh:mm a"
                            ampm={true}
                            minutesStep={5}
                            variant="inline"
                            inputVariant="standard"
                        />
                    </MuiPickersUtilsProvider>

                    <TextField
                        select
                        required
                        fullWidth
                        label="Recurrence Pattern"
                        value={scheduleForm.recurrence_pattern}
                        onChange={(e) =>
                            onFormChange({
                                ...scheduleForm,
                                recurrence_pattern: e.target.value,
                            })
                        }
                        error={showErrors && !scheduleForm.recurrence_pattern}
                        helperText={
                            showErrors && !scheduleForm.recurrence_pattern
                                ? 'Recurrence pattern is required'
                                : ''
                        }
                    >
                        {recurrencePatterns.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Container>
            </Container>

            {/* Right Column - Companies Table */}
            <Container
                style={{
                    flexDirection: 'column',
                    gap: theme.spacing(2),
                    flex: 1,
                }}
            >
                <TextField
                    fullWidth
                    placeholder="Search companies..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />

                <TableContainer
                    component={Paper}
                    style={{ maxHeight: 400, overflow: 'auto' }}
                >
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={
                                            scheduleForm.companies.length > 0 &&
                                            scheduleForm.companies.length <
                                                companies.length
                                        }
                                        checked={
                                            companies.length > 0 &&
                                            scheduleForm.companies.length ===
                                                companies.length
                                        }
                                        onChange={(e) => {
                                            onFormChange({
                                                ...scheduleForm,
                                                companies: e.target.checked
                                                    ? companies.map((c) => c.id)
                                                    : [],
                                            })
                                        }}
                                    />
                                </TableCell>
                                <TableCell
                                    style={{
                                        fontWeight: 'bold',
                                        color:
                                            showErrors &&
                                            !scheduleForm.companies.length
                                                ? theme.palette.error.main
                                                : '',
                                    }}
                                >
                                    Company Name *
                                </TableCell>
                                {/* Add more columns as needed */}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={2} align="center">
                                        Loading...
                                    </TableCell>
                                </TableRow>
                            ) : filteredCompanies.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={2} align="center">
                                        No companies found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredCompanies.map((company) => (
                                    <TableRow
                                        key={company.id}
                                        hover
                                        onClick={() =>
                                            handleCompanyToggle(company.id)
                                        }
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={scheduleForm.companies.includes(
                                                    company.id,
                                                )}
                                            />
                                        </TableCell>
                                        <TableCell>{company.name}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Container>
        </Container>
    )
}
